import checkAvailabilityTemplate from 'templates/liveTV/_messageCheckAvailability.tpl';
import noCoverageTemplate from 'templates/liveTV/_messageLocalNoCoverage.tpl';
import noLcpTemplate from 'templates/liveTV/_messageNoLcpLocalLive.tpl';
import modalFailLocationTemplate from 'templates/liveTV/_modalFailLocation.tpl';
import modalExceedStreamLimitTemplate from 'templates/liveTV/_modalExceedStreamLimit.tpl';
import MessageBox from 'components/messageBoxes/BaseLiveTVMessage';
import StationSelectedMessageBox from 'components/messageBoxes/StationSelectionLiveTVMessage';
import genericErrorTemplate from 'templates/liveTV/_modalGenericError.tpl';
import BaseModal from 'components/modals/BaseModal';
import HttpService from 'services/HttpService';
import LiveTVTrackingService from 'services/LiveTVTrackingService';
import { MessageResolver } from 'components/livetv/MessageResolver';
import GeoLocationService from 'services/GeoLocationService';
import LiveTVAvailabilityResponse from 'components/livetv/LiveTVAvailabilityResponse';
import StreamStation from 'POJOs/StreamStation';
import LiveTVStreamInfoResponse from 'components/livetv/LiveTVStreamInfoResponse';
import { getLocale } from 'services/LocalizationService';

const ENDPOINT = {
    AVAILABILITY_ENDPOINT: '/live-tv/check-availability/',
    STREAM_INFO_ENDPOINT: '/live-tv/stream-info/'
};

const AVAILABILITY_MSG = {
    AVAILABLE: 'AVAILABLE',
    EMPTY_CHANNEL: 'EMPTY_CHANNELS',
    USER_LOCATION: 'USER_LOCATION',
    LCP_LOCAL_LIVE: 'LCP_LOCAL_LIVE'
};

const STREAM_INFO_MSG = {
    TOO_MANY: 'TOO_MANY',
    NOT_AVAILABLE: 'NOT_AVAILABLE',
    MVPD: 'MVPD',
    UPSELL: 'UPSELL',
    ACCESS_DENIED: 'ACCESS_DENIED',
    NOT_AUTHORIZED: 'NOT_AUTHORIZED',
    BAD_REQUEST: 'BAD_REQUEST'
};

export default class LiveStreamAvailabilityService {
    constructor(streamContainer) {
        this.http = new HttpService();
        this.token = document.querySelector('input[name="tk_vid"]').value;
        this.tracking = new LiveTVTrackingService();
        this.geoLocationService = new GeoLocationService();

        this.location = null;
        /**
         * @type {Array.<StreamStation>}
         */
        this.availableStations = [];

        this.streamContainer = streamContainer;

        this.messages = new MessageResolver();

        // Message Templates
        this.availabilityMessage = null;
        this.noCoverageMessage = null;
        this.stationSelectionMessage = null;
        this.failLocationModal = null;
        this.genericErrorModal = null;
    }

    get metaContainer() {
        if (!this._metaContainer) {
            this._metaContainer = this.streamContainer.parentNode.querySelector('.video__metadata');
        }

        return this._metaContainer;
    }

    static get ENDPOINT() {
        return ENDPOINT;
    }

    /**
     * @returns {Promise<null>}
     */
    async checkLocation() {
        this.tracking.trackLiveTVAvailabilityCheck();
        this.__displayAvailabilityMessage();

        try {
            this.location = await this.geoLocationService.fetchLocation();

            return this.location;
        } catch (e) {
            this.__onLocationFail();
            throw e;
        }
    }

    /**
     * @returns {Promise<Array<StreamStation>>}
     */
    async checkAvailability() {
        let resp;

        try {
            resp = await this.__requestAvailability();

            if (resp.success && resp.message === AVAILABILITY_MSG.AVAILABLE) {
                this.__onAvailabilitySuccess(resp);
                return this.availableStations;
            } else {
                throw new Error(`Check Availability: ${resp.message}`);
            }
        } catch (e) {
            this.__onAvailabilityError(resp?.message);
            throw e;
        }
    }

    // For dev testing on local and platypus
    get mockAllowed() {
        return !['www.', 'prevew.', 'shadow.', 'test-www.', 'stage.'].some((subDomain) => {
            return window.location.host.indexOf(subDomain) > -1;
        });
    }

    /**
     * Use in __requestAvailability for testing during development
     * @param resp
     */
    mockAvailability(resp) {
        if (this.mockAllowed) {
            const mock = new URLSearchParams(window.location.search).get('MOCK_AVAILABILITY');

            if (mock) {
                resp.data.message = mock;
                resp.data.success =
                    mock === AVAILABILITY_MSG.AVAILABLE || mock === AVAILABILITY_MSG.EMPTY_CHANNEL;
            }
        }
    }

    /**
     * Use in __requestStreamInfo for testing during development
     * @param resp
     * @returns {null}
     */
    mockStreamInfo(resp) {
        if (this.mockAllowed) {
            const mock = new URLSearchParams(window.location.search).get('MOCK_STREAM_INFO');

            if (mock) {
                resp.data.message = mock;
                resp.data.success = false;
            }
        }

        return null;
    }

    /**
     * @returns {Promise<LiveTVAvailabilityResponse>}
     * @private
     */
    async __requestAvailability() {
        const resp = await this.http.doPost(ENDPOINT.AVAILABILITY_ENDPOINT, {
            lat: this.location.coords.latitude,
            long: this.location.coords.longitude
        });

        return new LiveTVAvailabilityResponse(resp.data);
    }

    __onAvailabilitySuccess(resp) {

        this.__storeStations(resp);
        this.__closeAvailabilityMessage();
    }

    /**
     * @param resp {LiveTVAvailabilityResponse}
     * @returns {Array.<StreamStation>}
     */
    __storeStations(resp) {
        for (let key of Object.keys(resp.stations)) {
            let foundStation = new StreamStation(
                key,
                resp.stations[key],
                resp.media[key],
                null,
                resp.logos[key]
            );
            foundStation.scheduleEndpoint = resp.scheduleUrls[foundStation.station];
            foundStation.market = resp.market;
            foundStation.label = resp.label;
            this.availableStations.push(foundStation);
        }
    }

    __onAvailabilityError(availabilityMessage) {
        if (availabilityMessage === AVAILABILITY_MSG.EMPTY_CHANNEL) {
            this.__onEmptyChannel();
        } else if (availabilityMessage === AVAILABILITY_MSG.USER_LOCATION) {
            this.__onLocationFail();
        } else if (availabilityMessage === AVAILABILITY_MSG.LCP_LOCAL_LIVE) {
            this.__onLcpFail();
        } else {
            this.__onGenericError();
        }
    }

    /**
     * @returns {Promise<StreamStation>}
     */
    async selectStation() {
        let station;
        if (this.availableStations.length === 1) {
            // if there's only on station to choose from, automatically select it
            station = this.availableStations[0];
        } else {
            let localChannelSlug = $.cookie('localTvSlug');
            let iterator = -1;
            if (localChannelSlug) {
                this.availableStations.forEach(function callback(value, index) {
                    if (value.name.toLowerCase() == localChannelSlug) {
                        iterator = index;
                    }
                });
            }
            if (iterator >= 0) {
                station = this.availableStations[iterator];
            } else {
                
                let sortStations = this.availableStations.sort((a, b) => {
                    if (a.name < b.name)
                      return -1;
                    if (a.name > b.name)
                      return 1;
                    return 0;
                });
                station = sortStations[0];
            }
        }

        this.tracking.trackStationSelected('CBS (Local Station)', station);
        this.__updateMeta(station);

        return station;
    }

    /**
     * Displaying meta data for stream player
     * @param station
     * @private
     */
    __updateMeta(station) {
        if (station.assets) {
            let stationLogo = document.createElement('img');
            if (CBS.Registry.brand.name === 'PPLUS') {
                stationLogo.src = `https://wwwimage-us.pplusstatic.com/base/${station.assets.small_logo_selected}`;
            } else {
                stationLogo.src = `https://wwwimage-secure.cbsstatic.com/base/${station.assets.small_logo_selected}`;
            }
            this.metaContainer.querySelector('.station__logo').appendChild(stationLogo);
        }

        this.metaContainer.querySelector('p').innerHTML = station.name;
    }

    /**
     * Fetching stream info
     * @param station
     * @param callback
     * @returns {{success}|LiveTVStreamInfoResponse}
     */
    async getStreamInfo(station, callback = null) {
        let resp;

        try {
            resp = await this.__requestStreamInfo(station);

            if (resp && resp.success) {
                return resp;
            } else {
                throw new Error(resp.message);
            }
        } catch (e) {
            this.__onStreamInfoError(resp?.message);
            throw e;
        }
    }

    /**
     * @param station
     * @returns {Promise<LiveTVStreamInfoResponse>}
     * @private
     */
    async __requestStreamInfo(station) {
        let payload = {
            lat: this.location.coords.latitude,
            long: this.location.coords.longitude,
            station: station.station,
            media: station.media,
            tk_vid: this.token
        };

        const resp = await this.http.doPost(ENDPOINT.STREAM_INFO_ENDPOINT, payload);

        return new LiveTVStreamInfoResponse(resp.data);
    }

    __onStreamInfoError(streamInfoMessage) {
        if (
            streamInfoMessage === STREAM_INFO_MSG.ACCESS_DENIED ||
            streamInfoMessage === STREAM_INFO_MSG.NOT_AUTHORIZED
        ) {
            this.__onStreamInfoFailedUnauthorized();
        } else if (streamInfoMessage === STREAM_INFO_MSG.BAD_REQUEST) {
            this.__onStreamInfoFailedBadRequest();
        } else if (streamInfoMessage === STREAM_INFO_MSG.NOT_AVAILABLE) {
            this.__onStreamInfoFailedNotAvailable();
        } else if (streamInfoMessage === STREAM_INFO_MSG.TOO_MANY) {
            this.__onStreamInfoFailedExceedLimit();
        }
    }

    // Error Screens

    /**
     * Showing location fail, cuz user block location sharing and google geoloc also failed
     */
    __onLocationFail() {
        this.tracking.trackLiveTVAvailabilityStatus(false);

        this.__displayLocationFailMessage();
    }

    __onLcpFail() {
        this.tracking.trackLiveTVAvailabilityStatus(false);

        this.__displayLcpLocalLiveUpsellMessage();
    }

    /**
     * All generic unexpected error message
     */
    __onGenericError() {
        this.__displayGenericError();
    }

    /**
     * Showing local location no coverage message
     */
    __onEmptyChannel() {
        this.__displayNoCoverageMessage();
    }

    /**
     * Showing error message when viewer open too many stream screens
     */
    __onExceedStreamLimit() {
        this.__displayExceedStreamLimitMessage();
    }

    /**
     * Not authorized on requesting stream info
     * @private
     */
    __onStreamInfoFailedUnauthorized() {
        window.location.assign('/live-tv/');
    }

    /**
     * Stream info request is missing important params including geolocation
     * @private
     */
    __onStreamInfoFailedBadRequest() {
        this.__onLocationFail();
    }

    /**
     * Stream info request returning empty result
     * @private
     */
    __onStreamInfoFailedNotAvailable() {
        this.__onEmptyChannel();
    }

    /**
     * Viewer has open too many stream screens exceeding limit numbers
     * @private
     */
    __onStreamInfoFailedExceedLimit() {
        this.__onExceedStreamLimit();
    }

    __displayAvailabilityMessage() {
        if (!this.availabilityMessage) {
            this.availabilityMessage = new MessageBox(
                this.streamContainer,
                checkAvailabilityTemplate({
                    title: this.messages.getMessage('lt_check_availability_title'),
                    desc: this.messages.getMessage('lt_check_availability_desc'),
                    faqMessage: getLocale('questions_visit_our_faq_page_at', {
                        'url': getLocale('faq_link')
                    })
                })
            );
        }

        this.availabilityMessage.show();
    }

    __closeAvailabilityMessage() {
        if (this.availabilityMessage) {
            this.availabilityMessage.close();
            this.availabilityMessage = null;
        }
    }

    __displayStationSelectionMessage(onStationSelectedCallback) {
        const copy = {
            title: this.messages.getMessage('lt_station_selection_title')
        };

        this.stationSelectionMessage = new StationSelectedMessageBox(
            this.streamContainer,
            this.availableStations,
            copy
        );

        this.stationSelectionMessage.onStationSelected((stationNum) => {
            let station = null;

            for (let s of this.availableStations) {
                if (parseInt(s.station) === parseInt(stationNum)) {
                    station = s;
                    break;
                }
            }

            this.__closeStationSelectionMessage();

            onStationSelectedCallback(station);
        });

        this.stationSelectionMessage.show();
    }

    __closeStationSelectionMessage() {
        if (this.stationSelectionMessage) {
            this.stationSelectionMessage.close();
            this.stationSelectionMessage = null;
        }
    }

    __displayNoCoverageMessage() {
        if (!this.noCoverageMessage) {
            this.noCoverageMessage = new MessageBox(
                this.streamContainer,
                noCoverageTemplate({
                    title: this.messages.getMessage('lt_no_coverage_title'),
                    desc: this.messages.getMessage('lt_no_coverage_desc'),
                    cta: {
                        copy: this.messages.getMessage('lt_no_coverage_cta'),
                        url: this.messages.getMessage('lt_no_coverage_cta_url'),
                        aaLink: 'locked video||sign in with tv provider|CBS (Local Station)'
                    }
                })
            );
        }

        this.noCoverageMessage.show();
    }

    __displayLcpLocalLiveUpsellMessage() {
        if (!this.noCoverageMessage) {
            this.noCoverageMessage = new MessageBox(
                this.streamContainer,
                noLcpTemplate({
                    title: this.messages.getMessage('lt_lcp_no_local_title'),
                    cta: {
                        copy: this.messages.getMessage('lt_lcp_no_local_cta'),
                        url: this.messages.getMessage('lt_lcp_no_local_url'),
                        aaLink: 'locked video||sign in with tv provider|CBS (Local Station)'
                    }
                })
            );
        }

        this.noCoverageMessage.show();
    }

    __displayLocationFailMessage() {
        if (!this.failLocationModal) {
            this.failLocationModal = new BaseModal(
                modalFailLocationTemplate({
                    title: this.messages.getMessage('lt_location_failed_title'),
                    desc: this.messages.getMessage('lt_location_failed_desc')
                })
            );
        }

        this.failLocationModal.show();
    }

    __displayExceedStreamLimitMessage() {
        // taking stream away from ya bruh
        document.querySelector('.video__player-wrapper').innerHTML = '';

        if (!this.exceedStreamLimitModal) {
            this.exceedStreamLimitModal = new BaseModal(
                modalExceedStreamLimitTemplate({
                    title: this.messages.getMessage('lt_exceed_limit_title'),
                    desc: this.messages.getMessage('lt_exceed_limit_desc')
                })
            );
            this.exceedStreamLimitModal.setDismissible(false);
        }

        this.exceedStreamLimitModal.show();
    }

    __displayGenericError() {
        if (!this.genericErrorModal) {
            this.genericErrorModal = new BaseModal(genericErrorTemplate());
        }

        this.genericErrorModal.show();
    }
}
